import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError } from '../../../../api/client';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import type { SectorName, SectorTemplateSummaryResponse } from '../api';
import { useSectorTemplates } from '../hooks';

/**
 * Catalog gallery (Phase 8 Wave 8.E). Lists every sector template, filterable by sector via a
 * pill bar. Each card links to `/document-types/templates/:id` for the preview + instantiate flow.
 */
export function SectorTemplateGalleryPage(): JSX.Element {
  const [sector, setSector] = useState<SectorName | undefined>(undefined);
  const [q, setQ] = useState('');
  const { data, isLoading, isError, error } = useSectorTemplates(sector, q);

  const grouped = useMemo(() => {
    const buckets = new Map<SectorName, SectorTemplateSummaryResponse[]>();
    for (const template of data ?? []) {
      const list = buckets.get(template.sector) ?? [];
      list.push(template);
      buckets.set(template.sector, list);
    }
    return buckets;
  }, [data]);

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Plantillas por sector</h1>
          <p className="mt-1 text-sm text-slate-500">
            Empieza con un esquema preconfigurado. Instanciarlo crea un borrador en tu tenant que puedes
            adaptar antes de publicar.
          </p>
        </div>
        <Link to="/document-types" className="text-sm text-slate-500 hover:underline">
          ← Volver al catálogo
        </Link>
      </header>

      <SectorPillBar selected={sector} onSelect={setSector} />

      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Buscar plantilla por nombre o descripción…"
        aria-label="Buscar plantilla"
        className="input max-w-md"
        data-testid="template-search"
      />

      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : isError ? (
        <ErrorBanner error={error} />
      ) : (data ?? []).length === 0 ? (
        <EmptyState />
      ) : (
        <div className="space-y-8">
          {Array.from(grouped.entries()).map(([sectorName, entries]) => (
            <section key={sectorName} aria-labelledby={`sector-${sectorName}`}>
              <h2 id={`sector-${sectorName}`} className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
                {entries[0]?.sectorLabel ?? sectorName}
              </h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {entries.map((template) => (
                  <TemplateCard key={template.id} template={template} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

const PILLS: ReadonlyArray<SectorName | undefined> = [
  undefined,
  'Generic',
  'Logistics',
  'Finance',
  'Ecommerce',
  'Healthcare',
];

function SectorPillBar(props: {
  selected: SectorName | undefined;
  onSelect: (sector: SectorName | undefined) => void;
}): JSX.Element {
  return (
    <nav aria-label="Filtrar por sector" className="flex flex-wrap gap-2">
      {PILLS.map((sectorName) => {
        const active = props.selected === sectorName;
        const label = sectorName ?? 'Todos';
        return (
          <button
            key={label}
            type="button"
            onClick={() => props.onSelect(sectorName)}
            aria-pressed={active}
            className={
              'rounded-full px-3 py-1 text-xs font-medium ' +
              (active
                ? 'bg-primary text-white'
                : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50')
            }
          >
            {label}
          </button>
        );
      })}
    </nav>
  );
}

function TemplateCard({ template }: { template: SectorTemplateSummaryResponse }): JSX.Element {
  return (
    <Link
      to={`/document-types/templates/${encodeURIComponent(template.id)}`}
      className="card flex flex-col gap-2 p-4 transition hover:border-primary"
      data-testid={`template-card-${template.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-base font-semibold text-slate-900">{template.name}</h3>
        <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
          {template.locale}
        </span>
      </div>
      <p className="text-sm text-slate-500">{template.description}</p>
      {template.previewFields.length > 0 ? (
        <p className="font-mono text-xs text-slate-500">
          {template.previewFields.join(' · ')}
          {template.fieldCount > template.previewFields.length ? ' …' : ''}
        </p>
      ) : null}
      <p className="mt-auto text-xs text-slate-400">{template.fieldCount} campos</p>
    </Link>
  );
}

function EmptyState(): JSX.Element {
  return (
    <div className="card flex flex-col items-center justify-center px-6 py-12 text-center">
      <h2 className="text-base font-semibold text-slate-900">No hay plantillas para este sector.</h2>
      <p className="mt-1 text-sm text-slate-500">
        Selecciona otro sector o explora todo el catálogo desde el filtro de arriba.
      </p>
    </div>
  );
}

function ErrorBanner({ error }: { error: unknown }): JSX.Element {
  const message = error instanceof ApiError ? error.message : 'No se pudo cargar el catálogo.';
  return (
    <div role="alert" className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
      {message}
    </div>
  );
}

export type { SectorTemplateSummaryResponse };
